import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from 'react';

import { TeamMilestones } from '@/config/constants';
import { GetMyTeamQuery, useGetMyTeamQuery } from '@/graphql';
import {
	PROVIDER_CACHE_KEYS,
	readProviderCache,
	readProviderCacheUpdatedAt,
	writeProviderCache,
} from '@/helpers/providerCache';
import { RefetchOptions, RefetchQueryFilters } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { isNil } from 'ramda';

import { LoadingResource, useLoading } from '../LoadingProvider';
import { useSetlists } from '../SetlistsProvider';
import { useTeamSongs } from '../TeamSongsProvider';

export type TeamMembership = GetMyTeamQuery['getMyTeam']['members'][number];

export type UserWithIsActive = TeamMembership['user'] & {
	isActive?: boolean;
};

type Team = Omit<GetMyTeamQuery['getMyTeam'], 'members'> & {
	members: (Omit<TeamMembership, 'user'> & {
		user: UserWithIsActive;
	})[];
};

export interface TeamContext {
	isLoading: boolean;
	team: Team;
	setTeam: React.Dispatch<React.SetStateAction<Team>>;
	refetch: <TPageData>(
		options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined,
	) => Promise<any>;
	findTeamMembersByUserId: (userId: string) => UserWithIsActive | undefined;
	hasMilestones: (milestones: TeamMilestones[]) => boolean;
}

const defaultTeam: TeamContext['team'] = {
	name: '',
	id: '',
	members: [],
	subscriptionStatus: false,
	inviteLinkCode: 'loading',
};

const defaultValue: TeamContext = {
	isLoading: false,
	team: defaultTeam,
	setTeam: () => {},
	refetch: async () => defaultTeam,
	findTeamMembersByUserId: () => undefined,
	hasMilestones: () => false,
};

const TeamContext = createContext<TeamContext>(defaultValue);

export function useTeam(): TeamContext {
	const context = useContext(TeamContext);
	if (isNil(context)) {
		throw new Error(`useTeam must be used within a TeamProvider`);
	}

	return context;
}

const calculateActiveStatus = (members: Team['members']): Team['members'] => {
	const comparisonTime = new Date().getTime() - 2 * 60 * 1000;

	return members.map((m) => {
		const lastSeenAtTime = new Date(m.user.lastSeenAt).getTime();
		return {
			...m,
			user: {
				...m.user,
				isActive: lastSeenAtTime > comparisonTime,
			},
		};
	});
};

export const TeamProvider: React.FC = ({ children }) => {
	const { setLoaded } = useLoading();
	const { enqueueSnackbar } = useSnackbar();
	const { teamSongs } = useTeamSongs();
	const { setlists } = useSetlists();
	const cachedTeam = readProviderCache<GetMyTeamQuery>(PROVIDER_CACHE_KEYS.team);
	const { data, isLoading, error, refetch } = useGetMyTeamQuery(
		{},
		{
			initialData: cachedTeam ?? undefined,
			initialDataUpdatedAt: readProviderCacheUpdatedAt(
				PROVIDER_CACHE_KEYS.team,
			),
			refetchOnMount: true,
			refetchOnWindowFocus: true,
		},
	);
	const [teamState, setTeamState] = useState<Team>(() => {
		const team = cachedTeam?.getMyTeam;
		if (!team) {
			return defaultValue.team;
		}

		return {
			...team,
			members: calculateActiveStatus(team.members),
		};
	});

	useEffect(() => {
		if (data?.getMyTeam && !isLoading) {
			setTeamState({
				...data.getMyTeam,
				members: calculateActiveStatus(data.getMyTeam.members),
			});
			writeProviderCache(PROVIDER_CACHE_KEYS.team, data);
		}
	}, [isLoading, data, setTeamState]);

	useEffect(() => {
		if (teamState.members.length) {
			const interval = setInterval(() => {
				// console.log('Recalculating active status...');
				setTeamState((team) => ({
					...team,
					members: calculateActiveStatus(team.members),
				}));
			}, 60 * 1000);

			// Cleanup
			return () => {
				clearInterval(interval);
			};
		}
	}, [teamState]);

	useEffect(() => {
		if (!isLoading) {
			setLoaded(LoadingResource.TEAM);
		}
	}, [isLoading, setLoaded]);

	const hasMilestones = useCallback(
		(milestones: TeamMilestones[]) => {
			const completedMilestones: TeamMilestones[] = [];
			if (teamSongs.length > 9) {
				completedMilestones.push(TeamMilestones.ADDED_INITIAL_SONGS);
			}
			if (setlists.length) {
				completedMilestones.push(TeamMilestones.CREATED_FIRST_SETLIST);
			}
			return milestones.every((milestone) =>
				completedMilestones.includes(milestone),
			);
		},
		[setlists.length, teamSongs.length],
	);

	const findTeamMembersByUserId = (userId: string) =>
		teamState.members.find((m) => m.user.id === userId)?.user;

	useEffect(() => {
		if (error && !teamState.id) {
			enqueueSnackbar('Failed to load team', {
				variant: 'warning',
			});
		}
	}, [enqueueSnackbar, error, teamState.id]);

	return (
		<TeamContext.Provider
			value={{
				isLoading,
				team: teamState,
				setTeam: setTeamState,
				refetch,
				findTeamMembersByUserId,
				hasMilestones,
			}}
		>
			{children}
		</TeamContext.Provider>
	);
};

export default TeamProvider;
